"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, Calendar, ArrowRight, CircleCheck as CheckCircle2, MapPin, Sparkles } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqData = [{
  question: "Why is Local SEO critical for businesses in Carlisle and Cumbria?",
  answer: "When customers in Carlisle search for your services, over 70% click on the Google Map Pack or top local organic listings. Carlisle's regional economy relies heavily on local search intent, whether you serve city-center clients or surrounding Cumbrian communities. Local SEO ensures you capture high-intent local traffic directly when decision-makers are looking to buy."
}, {
  question: "How quickly can my business in Carlisle see ranking improvements?",
  answer: "Google Business Profile optimizations and technical local fixes often show initial movements within 4 to 8 weeks. Comprehensive local authority building, citation cleanup, and localized content strategies typically hit peak momentum within 3 to 6 months. During our consultation, I will give you a clear, realistic timeframe based on your specific Carlisle competitors."
}, {
  question: "Will I work directly with you, Yassine, or an account manager?",
  answer: "You work directly with me—Yassine Essaadi. Unlike traditional digital marketing agencies that pass your account to junior staff after signing, I handle every strategic audit, keyword strategy, profile optimization, and reporting call personally. You get senior expertise with no account management layer."
}, {
  question: "What is the difference between organic Local SEO and Google Maps optimization?",
  answer: "Google Maps optimization targets the coveted 3-Pack box at the top of Google results, driven by physical location, profile completeness, local reviews, and proximity signals. Organic Local SEO focuses on ranking your website pages directly below the map box for Carlisle-specific keywords. My strategies integrate both to dominate the full search page."
}, {
  question: "How do you optimize my Google Business Profile for Carlisle searchers?",
  answer: "I perform a full audit of your primary and secondary categories, craft localized service descriptions, structure localized GEO tags, refine business hour attributes, establish a review acquisition framework, and regularly optimize your profile's media and Q&A sections to signals Google trusts."
}, {
  question: "Do I need a physical storefront in Carlisle to rank locally?",
  answer: "Not necessarily. If you operate as a Service Area Business (SAB)—such as a contractor, consultant, or home service provider covering Carlisle, Penrith, and North Cumbria—we can configure your Google Business Profile as a service area business to rank strongly without publicly disclosing a residential address."
}, {
  question: "How do you handle local citations and NAP consistency across Cumbrian directories?",
  answer: "Name, Address, and Phone number (NAP) consistency is fundamental for local search trust. I manually review and correct your existing business listings across key UK and Cumbria-specific directories, removing duplicates and ensuring every citation matches your verified Google Business Profile details precisely."
}, {
  question: "Can Local SEO help my business attract customers beyond central Carlisle?",
  answer: "Yes. By building dedicated service area landing pages and targeted localized content, I help your business capture search traffic across surrounding towns like Penrith, Workington, Brampton, and broader Cumbria without diluting your main Carlisle presence."
}, {
  question: "How do you measure the ROI of a Carlisle Local SEO campaign?",
  answer: "We track concrete business outcomes rather than vanity metrics alone: organic phone calls, Google Maps direction requests, contact form completions, Carlisle keyword positions, and organic website sessions. Every monthly report connects ranking progress directly to real business inquiries."
}, {
  question: "What makes your freelance consultancy different from a typical agency?",
  answer: "Agencies often rely on bloated overheads, generic template checklists, and high staff turnover. As an independent consultant, I offer bespoke strategies, hyper-focused attention on your campaign, direct communication, and flexible execution tailored specifically to your growth targets."
}, {
  question: "How do online reviews impact my local search rankings in Carlisle?",
  answer: "Reviews are a major local ranking factor and a decisive driver for conversion. I help you implement an ethical review generation framework that encourages happy Carlisle clients to leave keyword-rich Google reviews, while guiding you on how to respond effectively to boost local engagement signals."
}, {
  question: "What is included in your Carlisle Local SEO audit and strategy roadmap?",
  answer: "My comprehensive audit evaluates your Google Business Profile setup, local backlink profile, competitor positions in Carlisle, technical website health, NAP consistency, and localized content gaps. You receive a clear, prioritized execution roadmap with no fluff or jargon."
}];
export default function Faq() {
  const [openItem, setOpenItem] = useState<string | undefined>("item-0");
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseofaq" data-nav="light" className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      <div className="absolute top-0 right-0 -translate-y-12 translate-x-12 w-96 h-96 rounded-full bg-secondary/40 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 translate-y-12 -translate-x-12 w-96 h-96 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={fadeInVariants} className="text-center max-w-3xl mx-auto mb-14 lg:mb-20">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3.5 py-1 text-xs sm:text-sm font-medium gap-1.5 rounded-full">
              <MapPin className="size-3.5 text-primary" />
              <span>Carlisle &amp; Cumbria Local SEO</span>
            </Badge>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight mb-5">
            Frequently Asked Questions About <span className="text-primary">Local SEO</span>
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Straightforward, expert answers on dominating local search, Google Map Packs, and organic rankings in Carlisle. You work directly with me—no account managers or generic templates.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} variants={fadeInVariants} className="max-w-4xl mx-auto mb-16">
          <Accordion type="single" collapsible value={openItem} onValueChange={setOpenItem} className="space-y-4">
            {faqData.map((item, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="bg-card border border-border rounded-xl px-5 sm:px-7 py-1 shadow-xs transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-md overflow-hidden">
                <AccordionTrigger className="hover:no-underline py-5 text-left transition-colors group">
                  <div className="flex items-start gap-3 sm:gap-4 pr-2">
                    <HelpCircle className="size-5 sm:size-6 text-primary shrink-0 mt-0.5 transition-transform group-hover:scale-110" />
                    <span className="text-base sm:text-lg font-semibold text-primary group-hover:text-primary/90 leading-snug">
                      {item.question}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-1 pb-6 pl-8 sm:pl-10 pr-2 text-muted-foreground text-sm sm:text-base leading-relaxed border-t border-border/50 mt-1">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={fadeInVariants} className="max-w-4xl mx-auto rounded-2xl bg-gradient-to-br from-card via-card to-secondary/30 border border-border p-8 sm:p-10 shadow-lg text-center relative overflow-hidden">
          <div className="absolute top-3 right-3 text-primary/10 pointer-events-none">
            <Sparkles className="size-28" />
          </div>

          <div className="relative z-10 max-w-2xl mx-auto space-y-5">
            <Badge variant="outline" className="bg-background/80 border-primary/20 text-primary px-3 py-1 text-xs rounded-full inline-flex items-center gap-1.5">
              <CheckCircle2 className="size-3.5 text-primary" />
              <span>Direct Consultant Access</span>
            </Badge>

            <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground">
              Have a Specific Local SEO Question for Your Carlisle Business?
            </h3>

            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              Book a free 30-minute discovery consultation with me. I will evaluate your current Google local rankings and provide actionable recommendations tailored to your market.
            </p>

            <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" className="w-full sm:w-auto font-medium px-7 shadow-md group" asChild>
                <Link to="/ContactPage">
                  <SafeHydrate><Calendar className="size-4 mr-2" /></SafeHydrate>
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" className="w-full sm:w-auto font-medium px-6" asChild>
                <Link to="/CaseStudiesPage">
                  <MessageSquare className="size-4 mr-2 text-primary" />
                  <span>Explore My Case Studies</span>
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}